/**
 * Shared reviewer-routing actions (04-10-2026).
 *
 * The Review Dashboard and the request page both let a reviewer take work on,
 * and the wording of the accept confirmation is a product requirement: the
 * reviewer must be told, BEFORE accepting, that an accepted request can no
 * longer be disowned. Keeping the dialogs here means the two surfaces can
 * never drift apart.
 */
import { Modal, message as antMessage } from "antd";
import {
  CheckCircleOutlined,
  ExclamationCircleOutlined,
} from "@ant-design/icons";
import { postData } from "../../api/apiService";
import { MANAGED_SERVICE } from "../../api/endpointMap";

const errText = (e, fallback) =>
  typeof e === "string" ? e : e?.detail || e?.message || fallback;

/** Accept = commit to the request. Confirmation states the one-way door. */
export const acceptRequestConfirm = (requestId, { onDone, onBusy } = {}) =>
  Modal.confirm({
    title: "Accept this request?",
    icon: <CheckCircleOutlined style={{ color: "#16a34a" }} />,
    content: (
      <div className="text-sm">
        <p className="mb-2">
          The request will be assigned to you. You will be responsible for
          generation of response to this notice.
        </p>
        <p className="mb-0 text-amber-700">
          Once you accept, you cannot disown this request. If you are not going
          to work on it, disown it instead so another reviewer can pick it up.
        </p>
      </div>
    ),
    okText: "Accept",
    onOk: async () => {
      onBusy?.(true);
      try {
        await postData(MANAGED_SERVICE.ACCEPT(requestId), {});
        antMessage.success("Request accepted");
        await onDone?.();
      } catch (e) {
        antMessage.error(errText(e, "Accept failed"));
      } finally {
        onBusy?.(false);
      }
    },
  });

/** Disown = hand an unaccepted request back to the portal's reviewers. */
export const disownRequestConfirm = (requestId, { onDone, onBusy } = {}) =>
  Modal.confirm({
    title: "Disown this request?",
    icon: <ExclamationCircleOutlined style={{ color: "#d97706" }} />,
    content:
      "It becomes open to every reviewer of this portal, who are notified by email. You can still pick it up again until someone else takes it.",
    okText: "Disown",
    okButtonProps: { danger: true },
    onOk: async () => {
      onBusy?.(true);
      try {
        await postData(MANAGED_SERVICE.DISOWN(requestId), {});
        antMessage.success("Request disowned");
        await onDone?.();
      } catch (e) {
        antMessage.error(errText(e, "Disown failed"));
      } finally {
        onBusy?.(false);
      }
    },
  });

/** Release = an ADMINISTRATOR hands someone else's request back to the pool.
 *  Same endpoint as disown, different act: this takes work off a colleague,
 *  including work they had already accepted, so it says who loses it and warns
 *  that any drafting they had started stays with the request. */
export const releaseRequestConfirm = (
  requestId, { holderName, onDone, onBusy } = {},
) =>
  Modal.confirm({
    title: "Release this request back to the pool?",
    icon: <ExclamationCircleOutlined style={{ color: "#d97706" }} />,
    content: (
      <span>
        {holderName ? `${holderName} loses this request` : "The current reviewer loses this request"}
        {" and is notified. It becomes open to every reviewer of this portal, "}
        {"who can then take it on. Anything already drafted stays with the request."}
      </span>
    ),
    okText: "Release",
    okButtonProps: { danger: true },
    onOk: async () => {
      onBusy?.(true);
      try {
        await postData(MANAGED_SERVICE.DISOWN(requestId), {});
        antMessage.success("Request released. It is open to the portal's reviewers.");
        await onDone?.();
      } catch (e) {
        antMessage.error(errText(e, "Release failed"));
      } finally {
        onBusy?.(false);
      }
    },
  });

/** Capture = "I'm working on it" on an open request (accept still required). */
export const captureRequest = async (requestId, { onDone, onBusy } = {}) => {
  onBusy?.(true);
  try {
    await postData(MANAGED_SERVICE.CAPTURE(requestId), {});
    antMessage.success("Assigned to you. Accept it to start work.");
    await onDone?.();
  } catch (e) {
    antMessage.error(errText(e, "Could not take this request"));
  } finally {
    onBusy?.(false);
  }
};
